<template>
  <div :class="mainClass"
       ref="simpleSelect"
  >
    <div :class="[headerClass, disabled ? `${headerClass}__disabled` : '', hasError ? 'has-error' : '',
                  open ? `${headerClass}__open` : `${headerClass}`]"
    >
      <slot
        name="selected_option"
        :disabled="disabled"
        :openList="openList"
        :closeList="closeList"
        :change="change"
        :value="selected"
        :label="selectedLabel"
        :opened="open"
      >
        <input :id="inputId"
               :class="[disabled ? `${inputClass}__disabled` : `${inputClass}`]"
               :placeholder="selectedLabel"
               v-model="search"
               :disabled="disabled"
               @focus="openList()"
               @blur="closeList()"
               :tabindex="tabindex"
               @keyup="change"
               ref="search"
               autocomplete="no"
        />
      </slot>

      <span v-if="!spinner"
            :class="[open ? `${caretClass}__open` : `${caretClass}`]"
            @mousedown.prevent="toggleOpen()"
      >
        <slot name="caret">
          <i class="fa fa-caret-down" />
        </slot>
      </span>
      <span v-else :class="`${caretClass}__spinner`">
        <slot name="spinner">
          <i class="fa fa-spinner fa-spin" />
        </slot>
      </span>
    </div>
    <div :class="listClass" v-show="open" ref="options">
      <ul v-show="open"
          @mousemove="resetPointerEvents"
      >
        <template v-for="(option, index) in data">
          <li @mousedown.prevent="update(option)"
              :key="index"
              @mouseover="updatePosition(index, option.$isDisabled)"
              @mouseleave="showSelect = false"
              :class="[isActive(option) ? 'select-option__active' : '',
                       atPosition(index) ? 'select-option__hover' : '',
                       option.$isDisabled ? 'select-option__disabled' : '',
                       itemClass]"
              ref="option"
          >
            <slot name="option" :option="option">
              <template v-if="typeof option === 'object'">
                <span :class="labelClass">{{ customLabel ? customLabel(option) : option[label] }}</span>
              </template>
              <template v-else>
                <span :class="labelClass">{{ option }}</span>
              </template>
            </slot>
            <template v-if="remove">
              <span class="remove-option pull-right"
                    @mousedown.prevent.stop="$emit('remove', option)"
              >
                <slot name="removeIcon">
                  <i class="fa fa-trash" />
                </slot>
              </span>
            </template>
            <span v-if="!hideDeselect">
              <span v-if="optionSelected(option)" class="selection">Deselect</span>
            </span>
            <template v-if="!hideSelect">
              <span class="simple-select-select-text" v-if="showSelect && atPosition(index)">
                Select
              </span>
            </template>
          </li>
        </template>
        <li v-if="data.length === 0" :class="itemClass">No results...</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: [Number, Boolean, String, Array], default: null },
    nullValue: { type: String, default: '' },
    remove: { type: Boolean, default: false },
    hideDeselect: { type: Boolean, default: false },
    hideSelect: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    spinner: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Select' },
    options: { type: [Array, Object], default: () => [] },
    label: { type: String, default: 'name' },
    customLabel: { type: [Function, String], default: '' },
    returnValue: { type: String, default: 'id' },
    mainClass: { type: String, default: 'simple-select' },
    headerClass: { type: String, default: 'simple-select-header' },
    inputClass: { type: String, default: 'simple-select-input' },
    inputId: { type: String, default: '' },
    caretClass: { type: String, default: 'simple-select-caret' },
    listClass: { type: String, default: 'simple-select-options' },
    itemClass: { type: String, default: 'select-option' },
    labelClass: { type: String, default: 'select-label' },
    tabindex: { type: String, default: '' },
  },
  data: () => ({
    open: false,
    position: 0,
    search: '',
    selected: '',
    showSelect: false,
  }),
  computed: {
    data() {
      const searchCallback = option => {
        return typeof option === 'object'
          ? (this.customLabel ? this.customLabel(option) : option[this.label])
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase())
          : option
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase());
      };
      if (this.selected) {
        let data = [];
        data = data.concat(this.options.filter(option => option === this.selected));
        data = data.concat(this.options.filter(option => option !== this.selected));
        return data.filter(searchCallback);
      }
      return this.options.filter(searchCallback);
    },
    searchInput() {
      return this.$refs.search ? this.$refs.search : this.$parent.$refs.search;
    },
    selectedLabel() {
      if (this.selected && this.options.indexOf(this.selected) !== -1) {
        if (typeof this.selected === 'object') {
          if (this.customLabel) {
            return this.customLabel(this.selected);
          }
          return this.selected[this.label];
        }
        return this.selected;
      }
      return this.placeholder;
    },
  },
  watch: {
    value(val) {
      this.setSelected(val);

      if (this.searchable) {
        this.search = this.value.toString();
      }
    },
    options() {
      if (this.value) {
        this.setSelected(this.value);
      }
    },
  },
  created() {
    this.selected = this.nullValue;
    if (this.value) {
      this.setSelected(this.value);
      if (this.searchable) {
        this.search = this.value.toString();
      }
      this.$emit('search', this.search);
    }
  },
  methods: {
    toggleOpen() {
      if (!this.disabled) {
        if (this.open) {
          this.closeList();
        }
        this.openList();
      }
    },
    openList() {
      this.resetPosition();
      this.open = true;
      this.searchInput.focus();
      window.addEventListener('keydown', this.keydown, false);
      this.$emit('search', this.search);
    },
    closeList() {
      this.$nextTick(() => {
        this.open = false;
        if (this.searchable) {
          if (this.value === null) {
            this.search = '';
          } else {
            this.search = this.value.toString();
          }
        } else {
          this.search = '';
        }
        this.searchInput.blur();
        window.removeEventListener('keydown', this.keydown, false);
        this.$emit('search', this.search);
        this.$emit('blur');
      });
    },
    update(option) {
      if (!option.$isDisabled) {
        this.updateSingle(option);
      }
    },
    updateSingle(option) {
      this.selected = this.optionSelected(option) ? this.nullValue : option;
      if (this.selected === this.nullValue) {
        this.$emit('input', this.nullValue);
      } else {
        this.$emit(
          'input',
          this.returnValue.length ? this.selected[this.returnValue] : this.selected,
        );
      }
      this.closeList();
    },
    isActive(option) {
      return this.optionSelected(option);
    },
    setSelected(val) {
      if (this.returnValue.length && val !== null) {
        this.selected = this.options.find(option => option[this.returnValue] === val);
      } else {
        this.selected = val;
      }
    },
    optionSelected(option) {
      if (typeof option === 'object') {
        return JSON.stringify(option) === JSON.stringify(this.selected);
      }
      return option === this.selected;
    },
    atPosition(index) {
      return this.position === index;
    },
    updatePosition(index, disabled = false) {
      if (!disabled) {
        this.position = index;
        this.showSelect = true;
      }
    },
    resetPosition() {
      this.position = 0;
    },
    getSelectedId() {
      return this.selected ? this.selected.id : '';
    },
    getSelectedRefId() {
      return this.selected ? this.selected.ref_id : '';
    },
    setSelectedId(val) {
      if (this.returnValue.length && val !== null) {
        this.selected = this.options.find(option => option[this.returnValue] === val);
        this.search = this.selected[this.returnValue];
      } else {
        this.selected = val;
      }
    },
    disablePointerEvents() {
      this.data.forEach((o, index) => {
        this.$refs.option[index].style.pointerEvents = 'none';
      });
    },
    resetPointerEvents() {
      this.data.forEach((o, index) => {
        this.$refs.option[index].style.pointerEvents = 'auto';
      });
    },
    keydown(e) {
      const key = e.key || e.keyCode || e.which || e.charCode;
      if (key === 'ArrowUp') {
        e.preventDefault();
        if (this.position > 0) {
          this.position -= 1;
        }
        this.disablePointerEvents();
        if (this.$refs.option[0].clientHeight * this.position < this.$refs.options.scrollTop) {
          this.$refs.options.scrollTop = this.$refs.option[0].clientHeight * this.position;
        }
      }
      if (key === 'ArrowDown') {
        e.preventDefault();
        if (this.position < this.data.length - 1) {
          this.position += 1;
        }
        this.disablePointerEvents();
        if (this.$refs.option[0].clientHeight * (this.position + 1) > 300) {
          this.$refs.options.scrollTop =
            this.$refs.option[0].clientHeight * (this.position + 1) - 300;
        }
      }
      if (key === 'Enter') {
        e.preventDefault();
        this.update(this.data[this.position]);
      }
    },
    change(e) {
      const key = e.key || e.keyCode || e.which || e.charCode;
      if (this.searchable && key !== 'Enter' && key !== 'ArrowDown' && key !== 'ArrowUp') {
        this.$emit('search', this.search);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/SimpleSelect.vue